import type { CompanyListSource, LastOpened } from '@app/core';
import type { VirtualRowsDirective } from './virtual-rows.directive';

/**
 * The selected rows of a table by key. "Select all loaded" adds the rows
 * loaded so far; the bulk actions on the whole query (e.g. "add all") do not
 * need a selection.
 */
export class RowSelection<T> {
  public rows: T[] = [];
  private keys = new Set<unknown>();

  constructor(private keyOf: (row: T) => unknown) {}

  public get size() {
    return this.rows.length;
  }

  public has(row: T) {
    return this.keys.has(this.keyOf(row));
  }

  public set(rows: T[]) {
    this.rows = rows;
    this.keys = new Set(rows.map(this.keyOf));
  }

  public toggle(row: T) {
    this.set(
      this.has(row)
        ? this.rows.filter((x) => this.keyOf(x) !== this.keyOf(row))
        : [...this.rows, row]
    );
  }

  /** All these rows are selected (and there are any). */
  public hasAll(rows: readonly T[]) {
    return (
      !!rows.length &&
      rows.length <= this.size &&
      rows.every((x) => this.has(x))
    );
  }

  /** Selects these rows, or deselects them when all are selected. */
  public toggleAll(rows: readonly T[]) {
    if (this.hasAll(rows)) {
      const drop = new Set(rows.map(this.keyOf));
      this.set(this.rows.filter((x) => !drop.has(this.keyOf(x))));
    } else {
      this.set([...this.rows, ...rows.filter((x) => !this.has(x))]);
    }
  }

  public clear() {
    this.set([]);
  }
}

/** The most rows the way back loads to reach the company opened last. */
export const RESTORE_MAX_ROWS = 5000;

/**
 * The index the company opened last has among the rows, loading the chunks
 * down to its remembered index first; -1 when it is not among them (any
 * more).
 */
export const restoreIndex = async <T extends object>(
  source: CompanyListSource<T>,
  last: LastOpened
): Promise<number> => {
  if (!last.id) {
    return -1;
  }
  const index = last.index;
  if (index !== null && index < RESTORE_MAX_ROWS) {
    await source.ensureLoaded(index);
    const row = source.items[index] as Record<string, unknown> | undefined;
    if (row && row[source.key] === last.id) {
      return index;
    }
  }
  return source.indexOf(last.id);
};

/**
 * Shows the company opened last again after the way back: loads the rows
 * down to it, renders them (`render`) and scrolls it into view at the scroll
 * offset the table had. Without the company only the offset is restored.
 */
export const restoreLastOpened = async <T extends object>(
  source: CompanyListSource<T>,
  rows: VirtualRowsDirective,
  last: LastOpened,
  render: () => void
) => {
  const index = await restoreIndex(source, last);
  render();
  if (index >= 0) {
    rows.reveal(index, last.offset);
  } else if (last.offset !== null) {
    rows.reveal(Math.floor(last.offset / rows.rowHeight), last.offset);
  }
};
